import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterDateConfig,
  FilterStringConfig,
} from '@asset-metrix/portal-gateway-client-js';
import {
  isNotNullOrUndefined,
  isPureArray,
  MultiSelect,
} from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';
import { MultiSelectFilterData } from '../../store/states/filterTypes';

export type MultiSelectFilterProps = {
  disabled?: boolean;
  filterData?: MultiSelectFilterData;
  entityId: string;
  filterKey: string;
  formatter: (
    value: string | boolean | null | undefined
  ) => string | boolean | null | undefined;
  isFetching: boolean;
  label?: ReactNode | ReactNode[];
  type: (FilterStringConfig | FilterDateConfig)['type'] | 'boolean';
};

export function MultiSelectFilter({
  disabled,
  filterData,
  entityId,
  filterKey,
  formatter,
  isFetching,
  label,
  type,
}: MultiSelectFilterProps) {
  const { t } = useTranslation();
  const [setFilterSelection, resetFilterSelection] = useStore(
    useShallow((state) => [
      state.setFilterSelection,
      state.resetFilterSelection,
    ])
  );

  const [value, setValue] = useState(filterData?.selection);

  const missingOptionsError = filterData?.data && filterData?.data.length === 0;

  useEffect(() => {
    setValue(filterData?.selection);
  }, [filterData?.selection]);

  const data =
    filterData?.data.reduce<{ label: string; value: string | boolean }[]>(
      (acc, option) => {
        const value = typeof option === 'string' ? option : option?.value;
        const label =
          (typeof option === 'string' ? undefined : option?.label) ??
          formatter(value) ??
          value;

        if (isNotNullOrUndefined(value)) {
          acc.push({
            label: String(label),
            value,
          });
        }
        return acc;
      },
      []
    ) ?? [];

  if (missingOptionsError) {
    console.warn(
      `There are no options available for '${filterKey}'. This is a data or configuration error.`
    );
  }

  return (
    <MultiSelect
      data={data}
      disabled={disabled || (!isFetching && data.length === 0)}
      inlineError={true}
      key={filterKey}
      label={label}
      title={data.length === 0 ? t('app.select.noOptionsAvailable') : undefined}
      loading={isFetching}
      maxVisibleTags={2}
      onChange={(val) => {
        if (isPureArray(val)) {
          return setValue(val);
        }

        throw new Error('MultiSelectFilter onChange called with impure array');
      }}
      onCommit={(value) => {
        if (value.length === 0) {
          return resetFilterSelection(entityId, filterKey);
        }

        if (isPureArray(value)) {
          return setFilterSelection(
            entityId,
            filterKey,
            type,
            'multi-select',
            value
          );
        }

        throw new Error('MultiSelectFilter onCommit called with impure array');
      }}
      placeholder={t('app.pageFilter.placeholder')}
      value={value?.filter(isNotNullOrUndefined) ?? []}
    />
  );
}
